// src/schemas/font-catalog.ts
import { Type } from "@sinclair/typebox";
var SAFE_FONTS = [
  "Arial",
  "Calibri",
  "Cambria",
  "Consolas",
  "Courier New",
  "Georgia",
  "Segoe UI",
  "Tahoma",
  "Times New Roman",
  "Trebuchet MS",
  "Verdana",
  "Helvetica",
  "Helvetica Neue",
  "Menlo",
  "Monaco"
];
function isSafeFont(name) {
  const lower = name.toLowerCase();
  return SAFE_FONTS.some((f) => f.toLowerCase() === lower);
}
var FontFamilyNameSchema = Type.String({
  description: "Font family name. Prefer a SAFE_FONTS entry (Arial, Calibri, Cambria, Consolas, Courier New, Georgia, Segoe UI, Tahoma, Times New Roman, Trebuchet MS, Verdana, Helvetica, Helvetica Neue, Menlo, Monaco) for zero-setup rendering. For any other font, register it in props.fontRegistry[] and reference it here by family. Unregistered non-safe names render with a host fallback.",
  examples: ["Arial", "Calibri", "Georgia", "Inter", "Roboto"]
});
var FontWeightSchema = Type.Number({
  minimum: 100,
  maximum: 900,
  description: "OpenType weight (100 thin ... 900 black). Default 400."
});
var FontItalicSchema = Type.Boolean({
  description: "Whether this source is italic. Default false."
});
var SafeFontSourceSchema = Type.Object(
  {
    kind: Type.Literal("safe"),
    family: Type.String({
      description: "A SAFE_FONTS name \u2014 installed with Office."
    })
  },
  {
    additionalProperties: false,
    description: "Office-installed font \u2014 no embedding"
  }
);
var GoogleFontSourceSchema = Type.Object(
  {
    kind: Type.Literal("google"),
    family: Type.String({
      description: 'Exact Google Fonts family name (e.g. "Inter").'
    }),
    weights: Type.Optional(
      Type.Array(FontWeightSchema, {
        description: "Weights to fetch. Default [400, 700]."
      })
    ),
    italics: Type.Optional(
      Type.Boolean({ description: "Include italic variants. Default false." })
    )
  },
  {
    additionalProperties: false,
    description: "Google Fonts \u2014 auto-fetched and embedded"
  }
);
var FileFontSourceSchema = Type.Object(
  {
    kind: Type.Literal("file"),
    path: Type.String({
      description: "Path to a .ttf/.otf file. Relative paths are resolved against the JSON document file."
    }),
    weight: Type.Optional(FontWeightSchema),
    italic: Type.Optional(FontItalicSchema)
  },
  { additionalProperties: false, description: "Local font file to embed" }
);
var UrlFontSourceSchema = Type.Object(
  {
    kind: Type.Literal("url"),
    url: Type.String({
      description: "HTTPS URL of a TTF or OTF file."
    }),
    weight: Type.Optional(FontWeightSchema),
    italic: Type.Optional(FontItalicSchema)
  },
  {
    additionalProperties: false,
    description: "Direct TTF/OTF URL (non-Google CDN)"
  }
);
var VariableFontSourceSchema = Type.Object(
  {
    kind: Type.Literal("variable"),
    url: Type.String({
      description: "HTTPS URL of a variable TTF (`fvar` axis table required)."
    }),
    weight: FontWeightSchema,
    italic: Type.Optional(FontItalicSchema),
    axes: Type.Optional(
      Type.Record(Type.String(), Type.Number(), {
        description: "Additional axis pin values (e.g. `{ ital: 1, opsz: 14 }`) merged on top of the derived `wght` pin. Uncommon \u2014 the `weight` field is usually enough."
      })
    )
  },
  {
    additionalProperties: false,
    description: "Variable-font instancer. The fetcher pins `wght` to `weight` (plus any `axes` overrides) and emits a clean static TTF, bypassing upstream distributions that collapse multiple static weights onto the same glyph geometry."
  }
);
var DataFontSourceSchema = Type.Object(
  {
    kind: Type.Literal("data"),
    data: Type.String({
      description: "Base64-encoded TTF/OTF or data: URL (data:font/ttf;base64,...). Makes the JSON self-contained at the cost of size."
    }),
    weight: Type.Optional(FontWeightSchema),
    italic: Type.Optional(FontItalicSchema)
  },
  { additionalProperties: false, description: "Inline base64 font \u2014 portable" }
);
var FontSourceSchema = Type.Union(
  [
    SafeFontSourceSchema,
    GoogleFontSourceSchema,
    FileFontSourceSchema,
    DataFontSourceSchema,
    UrlFontSourceSchema,
    VariableFontSourceSchema
  ],
  {
    description: 'A single font variant source. Use kind:"safe" for installed fonts, kind:"google" for Google Fonts, kind:"file" for local files, kind:"data" for base64, kind:"url" for direct HTTPS TTF/OTF URLs, kind:"variable" to instance a variable TTF at a specific weight.'
  }
);
var FontCategorySchema = Type.Union(
  [
    Type.Literal("sans"),
    Type.Literal("serif"),
    Type.Literal("mono"),
    Type.Literal("display"),
    Type.Literal("handwriting")
  ],
  { description: "Broad category used for fallback selection" }
);
var FontRegistryEntrySchema = Type.Object(
  {
    id: Type.String({
      description: 'Registry key. By convention, match the display family name ("Inter", "Roboto Slab").'
    }),
    family: Type.String({
      description: "Display family used in font.family / fontFace / theme.fonts.*. Usually identical to id."
    }),
    category: Type.Optional(FontCategorySchema),
    sources: Type.Array(FontSourceSchema, {
      minItems: 1,
      description: "One or more weight/style variants. List at least a regular (weight 400, italic false)."
    })
  },
  {
    additionalProperties: false,
    description: "A font registered for this document. Referenced by family from font.family, fontFace, and theme.fonts.*."
  }
);
var FontRegistrySchema = Type.Array(FontRegistryEntrySchema, {
  description: "Document-scoped font registry. Every non-safe font used in this document must be registered here."
});

// src/theme/design-system.ts
import { Type as Type2 } from "@sinclair/typebox";
var TYPE_ROLES = [
  "eyebrow",
  "display",
  "stat",
  "quote",
  "label",
  "footer",
  "tableHeader",
  "tableCell",
  "chartLabel",
  "tracker",
  "source"
];
var TypeRoleNameSchema = Type2.Union(
  TYPE_ROLES.map((name) => Type2.Literal(name))
);
var CANVASES = ["a4", "letter", "wide169", "standard43"];
var TextCaseSchema = Type2.Union([
  Type2.Literal("none"),
  Type2.Literal("upper"),
  Type2.Literal("smallCaps")
]);
var ColorTokenSchema = Type2.String({
  pattern: "^(#?[0-9A-Fa-f]{6}|[a-zA-Z][a-zA-Z0-9]*)$",
  description: "Six-digit hex or a theme color/palette token. References must resolve without cycles."
});
var PaletteSchema = Type2.Object(
  {
    rule: Type2.Optional(ColorTokenSchema),
    textMuted: Type2.Optional(ColorTokenSchema),
    onPrimary: Type2.Optional(ColorTokenSchema),
    surfaceInverse: Type2.Optional(ColorTokenSchema),
    positive: Type2.Optional(ColorTokenSchema),
    negative: Type2.Optional(ColorTokenSchema),
    chart: Type2.Optional(
      Type2.Array(ColorTokenSchema, { minItems: 1, maxItems: 12 })
    )
  },
  { additionalProperties: false }
);
var TypeRoleSchema = Type2.Object(
  {
    face: Type2.Optional(
      Type2.Union([
        Type2.Literal("heading"),
        Type2.Literal("body"),
        Type2.Literal("mono"),
        Type2.Literal("light")
      ])
    ),
    weight: Type2.Optional(Type2.Integer({ minimum: 100, maximum: 900 })),
    size: Type2.Optional(
      Type2.Number({
        minimum: 5,
        maximum: 200,
        description: "Explicit size in points; takes precedence over the canvas scale."
      })
    ),
    lineHeight: Type2.Optional(Type2.Number({ minimum: 0.5, maximum: 3 })),
    tracking: Type2.Optional(
      Type2.Number({ description: "Tracking in hundredths of an em." })
    ),
    case: Type2.Optional(TextCaseSchema),
    color: Type2.Optional(ColorTokenSchema),
    spaceBefore: Type2.Optional(Type2.Number({ minimum: 0 })),
    spaceAfter: Type2.Optional(Type2.Number({ minimum: 0 }))
  },
  { additionalProperties: false }
);
var TypeRolesSchema = Type2.Object(
  {
    eyebrow: Type2.Optional(TypeRoleSchema),
    display: Type2.Optional(TypeRoleSchema),
    stat: Type2.Optional(TypeRoleSchema),
    quote: Type2.Optional(TypeRoleSchema),
    label: Type2.Optional(TypeRoleSchema),
    footer: Type2.Optional(TypeRoleSchema),
    tableHeader: Type2.Optional(TypeRoleSchema),
    tableCell: Type2.Optional(TypeRoleSchema),
    chartLabel: Type2.Optional(TypeRoleSchema),
    tracker: Type2.Optional(TypeRoleSchema),
    source: Type2.Optional(TypeRoleSchema)
  },
  { additionalProperties: false }
);
var ScaleSchema = Type2.Object(
  {
    base: Type2.Number({ minimum: 5, maximum: 200 }),
    ratio: Type2.Optional(Type2.Number({ minimum: 1, maximum: 2 })),
    baselinePt: Type2.Optional(
      Type2.Number({ exclusiveMinimum: 0, maximum: 24 })
    )
  },
  { additionalProperties: false }
);
var TypographySchema = Type2.Object(
  {
    roles: Type2.Optional(TypeRolesSchema),
    scale: Type2.Optional(
      Type2.Object(
        {
          a4: Type2.Optional(ScaleSchema),
          letter: Type2.Optional(ScaleSchema),
          wide169: Type2.Optional(ScaleSchema),
          standard43: Type2.Optional(ScaleSchema)
        },
        { additionalProperties: false }
      )
    )
  },
  { additionalProperties: false }
);
var CanvasSpacingSchema = Type2.Object(
  {
    safeAreaIn: Type2.Optional(Type2.Number({ minimum: 0 })),
    gutterIn: Type2.Optional(Type2.Number({ minimum: 0 })),
    columns: Type2.Optional(Type2.Integer({ minimum: 1, maximum: 100 })),
    rows: Type2.Optional(Type2.Integer({ minimum: 1, maximum: 100 }))
  },
  { additionalProperties: false }
);
var DesignSpacingSchema = Type2.Object(
  {
    basePt: Type2.Optional(Type2.Number({ exclusiveMinimum: 0 })),
    blockGap: Type2.Optional(
      Type2.Object(
        {
          tight: Type2.Optional(Type2.Number({ minimum: 0 })),
          normal: Type2.Optional(Type2.Number({ minimum: 0 })),
          loose: Type2.Optional(Type2.Number({ minimum: 0 }))
        },
        { additionalProperties: false }
      )
    ),
    canvas: Type2.Optional(
      Type2.Object(
        {
          a4: Type2.Optional(CanvasSpacingSchema),
          letter: Type2.Optional(CanvasSpacingSchema),
          wide169: Type2.Optional(CanvasSpacingSchema),
          standard43: Type2.Optional(CanvasSpacingSchema)
        },
        { additionalProperties: false }
      )
    )
  },
  { additionalProperties: false }
);
var RecipeSchema = Type2.Object(
  {
    type: Type2.Optional(TypeRoleNameSchema),
    color: Type2.Optional(ColorTokenSchema),
    fill: Type2.Optional(ColorTokenSchema),
    rule: Type2.Optional(
      Type2.Object(
        {
          weightPt: Type2.Optional(Type2.Number({ minimum: 0 })),
          color: Type2.Optional(ColorTokenSchema)
        },
        { additionalProperties: false }
      )
    ),
    padPt: Type2.Optional(Type2.Number({ minimum: 0 })),
    alignment: Type2.Optional(
      Type2.Union([
        Type2.Literal("left"),
        Type2.Literal("center"),
        Type2.Literal("right")
      ])
    )
  },
  { additionalProperties: false }
);
var ChromeSchema = Type2.Object(
  {
    runningHead: Type2.Optional(RecipeSchema),
    tracker: Type2.Optional(RecipeSchema),
    actionTitle: Type2.Optional(RecipeSchema),
    keyTakeaways: Type2.Optional(RecipeSchema),
    sourceLine: Type2.Optional(RecipeSchema),
    confidentialFooter: Type2.Optional(RecipeSchema),
    logoSlot: Type2.Optional(RecipeSchema),
    cover: Type2.Optional(RecipeSchema)
  },
  {
    additionalProperties: false,
    description: "Visual recipes; consumers land in #361. No automatic content or presence requirements."
  }
);
var MotifSchema = Type2.Object(
  {
    kind: Type2.Union([
      Type2.Literal("none"),
      Type2.Literal("rule"),
      Type2.Literal("corner"),
      Type2.Literal("band")
    ]),
    color: Type2.Optional(ColorTokenSchema),
    weightPt: Type2.Optional(Type2.Number({ minimum: 0 })),
    placement: Type2.Optional(
      Type2.Union([
        Type2.Literal("top"),
        Type2.Literal("bottom"),
        Type2.Literal("left"),
        Type2.Literal("right"),
        Type2.Literal("topLeft"),
        Type2.Literal("topRight"),
        Type2.Literal("bottomLeft"),
        Type2.Literal("bottomRight")
      ])
    )
  },
  {
    additionalProperties: false,
    description: "At most one motif. Rendering consumers land in #361."
  }
);
var DesignSystemProperties = {
  palette: Type2.Optional(PaletteSchema),
  typography: Type2.Optional(TypographySchema),
  spacing: Type2.Optional(DesignSpacingSchema),
  chrome: Type2.Optional(ChromeSchema),
  motif: Type2.Optional(MotifSchema)
};
var DesignSystemSchema = Type2.Object(DesignSystemProperties, {
  additionalProperties: false
});
function designCanvas(format, size) {
  if (format === "docx") return size === "LETTER" ? "letter" : "a4";
  const ratio = typeof size === "object" ? size.width / size.height : 4 / 3;
  return Math.abs(ratio - 16 / 9) < Math.abs(ratio - 4 / 3) ? "wide169" : "standard43";
}
var ROLE_SCALE_STEPS = {
  display: 4,
  stat: 3,
  quote: 1,
  tableHeader: 0,
  tableCell: 0,
  label: -1,
  eyebrow: -1,
  chartLabel: -1,
  tracker: -1,
  footer: -2,
  source: -2
};
function scaledSize(scale, step) {
  if (step === 0) return scale.base;
  const baseline = scale.baselinePt ?? 4;
  const exact = scale.base * (scale.ratio ?? 1.25) ** step;
  return Math.max(5, Math.min(200, Math.round(exact / baseline) * baseline));
}
var SCALE_STEP_RANGE = { from: -2, to: 5 };
function typeScaleSizes(scale) {
  const sizes = /* @__PURE__ */ new Set();
  for (let step = SCALE_STEP_RANGE.from; step <= SCALE_STEP_RANGE.to; step += 1) {
    sizes.add(scaledSize(scale, step));
  }
  return [...sizes].sort((a, b) => a - b);
}
function paletteScalars(palette) {
  const scalars = {};
  for (const [key, value] of Object.entries(palette ?? {})) {
    if (typeof value === "string") scalars[key] = value;
  }
  return scalars;
}
function capsFormatting(textCase) {
  return textCase === "upper" ? { allCaps: true } : { smallCaps: textCase === "smallCaps" };
}
function resolveMotif(motif) {
  return motif && motif.kind !== "none" ? motif : void 0;
}
function resolveTypeRoles(system, canvas, base) {
  const scale = system.typography?.scale?.[canvas];
  const roles = {};
  for (const name of TYPE_ROLES) {
    const role = system.typography?.roles?.[name];
    if (!role) continue;
    roles[name] = {
      ...role,
      size: role.size ?? (scale ? scaledSize(scale, ROLE_SCALE_STEPS[name]) : base)
    };
  }
  return roles;
}
function designColors(colors, palette) {
  return { ...colors, ...paletteScalars(palette) };
}
function resolveDesignColor(value, colors) {
  const seen = /* @__PURE__ */ new Set();
  let current = value;
  while (current !== void 0 && !seen.has(current)) {
    if (!current.startsWith("#") && Object.hasOwn(colors, current)) {
      seen.add(current);
      current = colors[current];
    } else {
      return /^#?[0-9a-f]{6}$/i.test(current) ? current.replace(/^#/, "") : void 0;
    }
  }
  return void 0;
}
function validateDesignColors(system, colors) {
  const resolved = designColors(colors, system.palette);
  const entries = Object.entries(system.palette ?? {}).flatMap(
    ([key, value]) => Array.isArray(value) ? value.map((entry, i) => [`palette.${key}[${i}]`, entry]) : [[`palette.${key}`, value]]
  );
  for (const [name, role] of Object.entries(system.typography?.roles ?? {})) {
    if (role.color)
      entries.push([`typography.roles.${name}.color`, role.color]);
  }
  for (const [path, value] of entries) {
    if (value && !resolveDesignColor(value, resolved)) {
      throw new Error(
        `Unresolvable theme color at ${path}: "${value}" (unknown token or cycle)`
      );
    }
  }
}

export {
  SAFE_FONTS,
  isSafeFont,
  FontFamilyNameSchema,
  FontSourceSchema,
  FontRegistryEntrySchema,
  FontRegistrySchema,
  TYPE_ROLES,
  TypeRoleNameSchema,
  CANVASES,
  TextCaseSchema,
  PaletteSchema,
  TypeRoleSchema,
  TypeRolesSchema,
  TypographySchema,
  DesignSpacingSchema,
  ChromeSchema,
  MotifSchema,
  DesignSystemProperties,
  DesignSystemSchema,
  designCanvas,
  ROLE_SCALE_STEPS,
  typeScaleSizes,
  capsFormatting,
  resolveMotif,
  resolveTypeRoles,
  designColors,
  resolveDesignColor,
  validateDesignColors
};
//# sourceMappingURL=chunk-7ZEN5H5R.js.map